import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupAPI } from '../../api/client';
import { captureEvent } from '../../lib/posthog';
import type { AIProvider, TrackingMode, WizardState } from '../../types';
import { SetupProgress } from '../setup/SetupProgress';
import { ConnectWhatsAppStep } from '../setup/ConnectWhatsAppStep';
import { AIProviderStep } from '../setup/AIProviderStep';
import { ContactTrackingStep } from '../setup/ContactTrackingStep';
import { ReviewCompleteStep } from '../setup/ReviewCompleteStep';
import DataSharingStep from '../setup/DataSharingStep';
import { LanguageStep } from '../setup/LanguageStep';
import { ManagedWelcomeStep } from '../setup/ManagedWelcomeStep';
import { ManagedWhatsAppStep } from '../setup/ManagedWhatsAppStep';

const DEFAULT_STATE: WizardState = {
  step: 1,
  whatsappConnected: false,
  aiProvider: 'gemini',
  aiApiKey: '',
  aiModel: 'gemini-3-flash-preview',
  ollamaUrl: 'http://localhost:11434',
  trackingMode: 'opt_out',
  sharingTier: 0,
  timezone: '',
  phoneNumber: '',
  userLanguage: 'en',
};

const PERSISTED_KEYS: (keyof WizardState)[] = [
  'step',
  'aiProvider',
  'aiApiKey',
  'aiModel',
  'ollamaUrl',
  'trackingMode',
  'sharingTier',
  'timezone',
  'phoneNumber',
  'userLanguage',
];

export function SetupScreen() {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(DEFAULT_STATE);
  const [keyValidated, setKeyValidated] = useState(false);
  const [ready, setReady] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSave = useRef<Partial<WizardState>>({});

  const scheduleSave = useCallback((partial: Partial<WizardState>) => {
    const persisted = Object.fromEntries(
      Object.entries(partial).filter(([key]) => PERSISTED_KEYS.includes(key as keyof WizardState))
    ) as Partial<WizardState>;

    if (Object.keys(persisted).length === 0) {
      return;
    }

    pendingSave.current = { ...pendingSave.current, ...persisted };

    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }

    saveTimer.current = setTimeout(() => {
      const nextPayload = pendingSave.current;
      pendingSave.current = {};
      setupAPI.saveState(nextPayload).catch(() => {});
    }, 300);
  }, []);

  const updateState = useCallback((partial: Partial<WizardState>, persist = true) => {
    setState(prev => ({ ...prev, ...partial }));
    if (ready && persist) {
      scheduleSave(partial);
    }
  }, [ready, scheduleSave]);

  // Load state from real config + runtime status on mount
  useEffect(() => {
    setupAPI.loadState()
      .then(saved => {
        setState(prev => ({ ...prev, ...saved }));
      })
      .catch(() => {
        // Can't reach backend, proceed with defaults
      })
      .finally(() => {
        setReady(true);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    };
  }, []);

  const goToStep = useCallback((step: number, fromStep?: number) => {
    if (fromStep !== undefined && step < fromStep) {
      captureEvent('setup_step_back', { from_step: fromStep, to_step: step });
    } else if (fromStep !== undefined) {
      captureEvent('setup_step_completed', { step: fromStep });
    }
    captureEvent('setup_step_viewed', { step });
    updateState({ step });
  }, [updateState]);

  const handleWhatsAppConnected = useCallback(() => {
    updateState({ whatsappConnected: true }, false);
  }, [updateState]);

  function handleComplete() {
    navigate('/people', { replace: true });
  }

  const [managedStep, setManagedStep] = useState<'welcome' | 'whatsapp'>('welcome');

  async function handleManagedComplete() {
    try {
      const result = await setupAPI.complete({
        ai_provider: state.aiProvider,
        ai_api_key: '',
        ai_model: state.aiModel,
        tracking_mode: state.trackingMode,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        phone_number: '',
        user_language: state.userLanguage || navigator.language.split('-')[0] || 'en',
      });
      if (result.auth_token) {
        localStorage.setItem('clawer_auth_token', result.auth_token);
      }
      if (result.gateway_token) {
        localStorage.setItem('clawer_gateway_token', result.gateway_token);
      }
    } catch {
      // Non-blocking — navigate anyway
    }
    navigate('/people', { replace: true });
  }

  if (!ready) {
    return (
      <div className="min-h-[100dvh] bg-canvas flex items-center justify-center">
        <span className="material-icons-round text-accent text-3xl animate-spin">sync</span>
      </div>
    );
  }

  if (state.isManaged) {
    return (
      <div className="min-h-[100dvh] bg-canvas flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-raised max-w-md w-full p-6">
          {managedStep === 'welcome' && (
            <ManagedWelcomeStep
              displayName={state.displayName}
              onNext={() => setManagedStep('whatsapp')}
            />
          )}
          {managedStep === 'whatsapp' && (
            <ManagedWhatsAppStep
              whatsappConnected={state.whatsappConnected}
              onConnected={handleWhatsAppConnected}
              onComplete={() => void handleManagedComplete()}
              onSkip={() => void handleManagedComplete()}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-canvas flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-raised max-w-md w-full p-6">
        <SetupProgress currentStep={state.step} />

        {/* Step transitions */}
        <div className="relative">
          {state.step === 1 && (
            <ConnectWhatsAppStep
              whatsappConnected={state.whatsappConnected}
              onConnected={handleWhatsAppConnected}
              onNext={() => goToStep(2, 1)}
              onSkip={() => {
                captureEvent('setup_whatsapp_skipped', {});
                goToStep(2, 1);
              }}
            />
          )}

          {state.step === 2 && (
            <AIProviderStep
              provider={state.aiProvider}
              apiKey={state.aiApiKey}
              model={state.aiModel}
              ollamaUrl={state.ollamaUrl}
              keyValidated={keyValidated}
              onProviderChange={(p: AIProvider) =>
                updateState({ aiProvider: p })
              }
              onApiKeyChange={(k: string) =>
                updateState({ aiApiKey: k })
              }
              onModelChange={(m: string) =>
                updateState({ aiModel: m })
              }
              onOllamaUrlChange={(u: string) =>
                updateState({ ollamaUrl: u })
              }
              onKeyValidated={setKeyValidated}
              onNext={() => goToStep(3, 2)}
              onBack={() => goToStep(1, 2)}
            />
          )}

          {state.step === 3 && (
            <LanguageStep
              userLanguage={state.userLanguage}
              onUserLanguageChange={(lang: string) =>
                updateState({ userLanguage: lang })
              }
              onNext={() => goToStep(4, 3)}
              onBack={() => goToStep(2, 3)}
            />
          )}

          {state.step === 4 && (
            <ContactTrackingStep
              trackingMode={state.trackingMode}
              onTrackingChange={(m: TrackingMode) =>
                updateState({ trackingMode: m })
              }
              onNext={() => goToStep(5, 4)}
              onBack={() => goToStep(3, 4)}
            />
          )}

          {state.step === 5 && (
            <DataSharingStep
              sharingTier={state.sharingTier}
              onChange={(tier: number) => updateState({ sharingTier: tier })}
              onNext={() => goToStep(6, 5)}
              onBack={() => goToStep(4, 5)}
            />
          )}

          {state.step === 6 && (
            <ReviewCompleteStep
              whatsappConnected={state.whatsappConnected}
              aiProvider={state.aiProvider}
              aiApiKey={state.aiApiKey}
              aiModel={state.aiModel}
              ollamaUrl={state.ollamaUrl}
              keyValidated={keyValidated}
              trackingMode={state.trackingMode}
              sharingTier={state.sharingTier}
              timezone={state.timezone}
              phoneNumber={state.phoneNumber}
              userLanguage={state.userLanguage}
              onTimezoneChange={(tz: string) =>
                updateState({ timezone: tz })
              }
              onPhoneChange={(p: string) =>
                updateState({ phoneNumber: p })
              }
              onComplete={handleComplete}
              onBack={() => goToStep(5, 6)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SetupScreen;
